import React, { useMemo, useEffect, useState } from 'react';
import { useTable, useGlobalFilter, useFilters, usePagination, useSortBy } from 'react-table';
import { Link, useHistory, useParams } from 'react-router-dom';
import { Dropdown, Row, Col, Card, Tab, Nav, Spinner, Alert } from 'react-bootstrap';
import CreatePayrollModal from './CreatePayrollModal';
import EditPayrollModal from './EditPayrollModal';
import axios from '../../../../services/AxiosInstance';
import moment from 'moment';
import 'moment/locale/sv';
import { getApiUrl } from '../../../../utils/ApiUtils';
import PageHeader from '../../../layouts/PageHeader';
import { NoData } from '../../SharedComponents/NoData';
import {
  Calendar,
  CalendarDays,
  Users,
  CreditCard,
  Edit3,
  Plus,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Filter,
  Search,
  X
} from 'lucide-react';

const Payrolls = ({ commercialReadOnly = false }) => {
  const { companyId } = useParams();
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [payrollData, setPayrollData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  moment.locale('sv');

  // Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleShowModal = () => !commercialReadOnly && setShowModal(true);
  const handleCloseModal = () => setShowModal(false);

  const handleShowEditModal = (payroll) => {
    if (commercialReadOnly) return;
    console.log('Selected Payroll:', payroll);
    setSelectedPayroll(payroll);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedPayroll(null);
  };

  const handleSaveDate = async (data) => {
    if (commercialReadOnly) return;
    try {
      const getPayrollApiUrl = getApiUrl(`${companyId}/payroll`);

      await axios.post(getPayrollApiUrl, {
        year: data.year,
        month: data.month,
        payoutDate: data.payoutDate
      }, { withCredentials: true });

      await fetchPayrollData();
      handleCloseModal();
    } catch (error) {
      setError('Det gick inte att skapa lönekörningen');
    }
  };

  const handleUpdatePayoutDate = async (date) => {
    if (commercialReadOnly) return;
    try {
      if (!selectedPayroll) return;

      const updatePayrollApiUrl = getApiUrl(`${companyId}/payroll/${selectedPayroll.id}/payout-date`);

      const response = await axios.put(updatePayrollApiUrl, {
        payoutDate: date
      }, { withCredentials: true });

      if (response.data.status === 'success') {
        await fetchPayrollData();
        handleCloseEditModal();
      } else {
        setError(response.data.message || 'Det gick inte att uppdatera utbetalningsdatumet');
      }
    } catch (error) {
      console.error('Failed to update payout date:', error);
      const errorMessage = error.response?.data?.message || 'Det gick inte att uppdatera utbetalningsdatumet';
      setError(errorMessage);
    }
  };

  const fetchPayrollData = async () => {
    try {
      setLoading(true);
      const getPayrollsApiUrl = getApiUrl(`${companyId}/payrolls`);
      const response = await axios.get(getPayrollsApiUrl, { withCredentials: true });
      setPayrollData(response.data);
    } catch (error) {
      setError('Det gick inte att hämta lönekörningar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayrollData();
  }, []);

  const filteredData = useMemo(() => {
    if (!searchTerm) return payrollData;

    return payrollData.filter(payroll => {
      const periodText = `${moment().month(payroll.month - 1).format('MMMM')} ${payroll.year}`;
      return periodText.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payroll.year.toString().includes(searchTerm) ||
        moment().month(payroll.month - 1).format('MMMM').toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [payrollData, searchTerm]);

  const clearSearch = () => {
    setSearchTerm('');
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="loading-container">
          <div className="loading-content">
            <div className="loading-spinner"></div>
            <p className="loading-text">Laddar löneperioder...</p>
          </div>
        </div>
      );
    }

    if (payrollData.length === 0) {
      return (
        <div className="empty-state">
          <div className="empty-state-icon">
            <CalendarDays size={48} />
          </div>
          <h3 className="empty-state-title">Inga löneperioder än</h3>
          <p className="empty-state-description">
            Skapa din första lönekörning för att komma igång med lönehantering
          </p>
          <button
            disabled={commercialReadOnly} onClick={handleShowModal}
            className="action-button btn-primary"
          >
            <Plus size={16} />
            Skapa första lönekörningen
          </button>
        </div>
      );
    }

    return (
      <div className="payroll-table-container">
        <PayrollsTable
          data={filteredData}
          onEditPayoutDate={commercialReadOnly ? undefined : handleShowEditModal}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onClearSearch={clearSearch}
        />
      </div>
    );
  };

  return (
    <>
      <style>{`
        .payroll-container {
          min-height: 100vh;
          padding: 2rem 0;
        }
        
        .page-header {
          background: white;
          border-radius: 12px;
          padding: 2rem;
          margin-bottom: 2rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
        }
        
        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
        }
        
        .header-title {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }
        
        .header-title h1 {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1e293b;
          margin: 0;
        }
        
        .header-subtitle {
          color: #64748b;
          font-size: 0.875rem;
          margin: 0.25rem 0 0 0;
        }
        
        .action-button {
          border-radius: 8px;
          padding: 0.75rem 1.5rem;
          font-weight: 600;
          font-size: 0.875rem;
          border: none;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
        }
        
        .btn-primary {
          background: var(--bs-primary);
          color: white;
        }
        
        .btn-primary:hover {
          background: var(--bs-primary);
          transform: translateY(-1px);
        }
        
        .btn-primary:disabled {
          background: #94a3b8;
          cursor: not-allowed;
          transform: none;
        }
        
        .error-alert {
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          color: #dc2626;
        }
        
        .loading-container {
          background: white;
          border-radius: 12px;
          padding: 4rem 2rem;
          text-align: center;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
        }
        
        .loading-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
        }
        
        .loading-spinner {
          width: 32px;
          height: 32px;
          border: 3px solid #e5e7eb;
          border-top: 3px solid var(--bs-primary);
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        
        .loading-text {
          color: #64748b;
          font-size: 0.875rem;
          margin: 0;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .empty-state {
          background: white;
          border-radius: 12px;
          padding: 4rem 2rem;
          text-align: center;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
        }
        
        .empty-state-icon {
          color: #9ca3af;
          margin-bottom: 1.5rem;
          display: flex;
          justify-content: center;
        }
        
        .empty-state-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 0.5rem;
        }
        
        .empty-state-description {
          color: #64748b;
          font-size: 0.875rem;
          margin-bottom: 2rem;
          max-width: 400px;
          margin-left: auto;
          margin-right: auto;
        }
        
        .payroll-table-container {
          background: white;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
          overflow: hidden;
        }
        
        .table-header {
          background: #f8fafc;
          padding: 1.5rem 2rem;
          border-bottom: 1px solid #e5e7eb;
        }
        
        .search-container {
          position: relative;
          max-width: 400px;
        }
        
        .search-input {
          width: 100%;
          padding: 0.75rem 0.75rem 0.75rem 2.5rem;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 0.875rem;
          background: white;
          color: #374151;
          transition: all 0.2s ease;
        }
        
        .search-input:focus {
          outline: none;
          border-color: var(--bs-primary);
          box-shadow: 0 0 0 3px rgba(68, 129, 78, 0.1);
        }
        
        .search-icon {
          position: absolute;
          left: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          color: #6b7280;
        }
        
        .search-clear {
          position: absolute;
          right: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: #6b7280;
          cursor: pointer;
          padding: 0.25rem;
          border-radius: 4px;
          transition: all 0.2s ease;
        }
        
        .search-clear:hover {
          background: #f3f4f6;
          color: #374151;
        }
        
        .modern-table {
          width: 100%;
          border-collapse: collapse;
        }
        
        .modern-table th {
          background: #f8fafc;
          color: #374151;
          font-weight: 600;
          font-size: 0.875rem;
          padding: 1rem 1.5rem;
          text-align: left;
          border-bottom: 1px solid #e5e7eb;
          white-space: nowrap;
        }
        
        .modern-table th:hover {
          background: #f1f5f9;
        }
        
        .sort-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          cursor: pointer;
          user-select: none;
        }
        
        .sort-indicator {
          margin-left: 0.5rem;
          color: #9ca3af;
        }
        
        .modern-table td {
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #f1f5f9;
          vertical-align: middle;
        }
        
        .modern-table tbody tr {
          transition: all 0.2s ease;
          cursor: pointer;
        }
        
        .modern-table tbody tr:hover {
          background: #f8fafc;
        }
        
        .period-cell {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }
        
        .period-icon {
          color: var(--bs-primary);
          flex-shrink: 0;
        }
        
        .period-text {
          font-weight: 600;
          color: #1e293b;
        }
        
        .period-year {
          color: #64748b;
          font-size: 0.875rem;
        }
        
        .date-cell {
          color: #374151;
          font-weight: 500;
        }
        
        .payslip-badge {
          background: #dcfce7;
          color: #166534;
          padding: 0.25rem 0.75rem;
          border-radius: 9999px;
          font-size: 0.75rem;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
        }
        
        .action-cell {
          display: flex;
          justify-content: flex-end;
          align-items: center;
          gap: 0.5rem;
        }
        
        .action-icon {
          padding: 0.5rem;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
          background: white;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .action-icon:hover {
          background: #f9fafb;
          color: #var(--bs-primary);
          border-color: #d1d5db;
        }
        
        .pagination-container {
          background: #f8fafc;
          padding: 1rem 2rem;
          border-top: 1px solid #e5e7eb;
          display: flex;
          justify-content: between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
        }
        
        .pagination-info {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #64748b;
          font-size: 0.875rem;
        }
        
        .page-size-select {
          padding: 0.25rem 0.5rem;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 0.875rem;
          background: white;
        }
        
        .pagination-buttons {
          display: flex;
          gap: 0.25rem;
        }
        
        .pagination-btn {
          padding: 0.5rem 0.75rem;
          border: 1px solid #d1d5db;
          background: white;
          color: #374151;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 0.875rem;
        }
        
        .pagination-btn:hover:not(:disabled) {
          background: #f9fafb;
          border-color: #9ca3af;
        }
        
        .pagination-btn:disabled {
          color: #9ca3af;
          cursor: not-allowed;
        }
        
        /* Responsive design */
        @media (max-width: 768px) {
          .payroll-container {
            padding: 1rem 0;
          }
          
          .page-header {
            padding: 1.5rem;
            margin-bottom: 1rem;
          }
          
          .header-content {
            flex-direction: column;
            align-items: stretch;
          }
          
          .table-header {
            padding: 1rem;
          }
          
          .search-container {
            max-width: none;
          }
          
          .modern-table th,
          .modern-table td {
            padding: 0.75rem 1rem;
          }
          
          .pagination-container {
            flex-direction: column;
            align-items: stretch;
          }
          
          .pagination-buttons {
            justify-content: center;
          }
        }
      `}</style>

      <div className="payroll-container">

        <PageHeader
          icon={CalendarDays}
          title="Löneperioder"
          subtitle="Hantera löneperioder och utbetalningsdatum för ditt företag."
          actions={
            <button
              className="action-button btn-primary"
              onClick={handleShowModal}
              disabled={commercialReadOnly || loading}
            >
              <Plus size={16} />
              Ny lönekörning
            </button>
          }
        />

        {/* Error Alert */}
        {error && (
          <div className="error-alert">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {/* Content */}
        {renderContent()}
      </div>


      <CreatePayrollModal
        show={showModal}
        handleClose={handleCloseModal}
        handleSave={handleSaveDate}
      />

      <EditPayrollModal
        show={showEditModal}
        handleClose={handleCloseEditModal}
        handleSave={handleUpdatePayoutDate}
        initialDate={selectedPayroll?.payoutDate}
        title="Ändra utbetalningsdatum"
        payrollPeriod={selectedPayroll ? { year: selectedPayroll.year, month: selectedPayroll.month } : null}
      />
    </>
  );
};

const PayrollsTable = ({ data, onEditPayoutDate, searchTerm, onSearchChange, onClearSearch }) => {
  const { companyId } = useParams();
  const history = useHistory();

  const columns = useMemo(() => [
    {
      Header: 'Period',
      accessor: 'perioden',
      Cell: ({ row }) => (
        <div className="period-cell">
          <Calendar size={16} className="period-icon" />
          <div>
            <div className="period-text">
              {moment().month(row.original.month - 1).format('MMMM')}
            </div>
            <div className="period-year">{row.original.year}</div>
          </div>
        </div>
      ),
      sortType: (rowA, rowB) => {
        const dateA = new Date(rowA.original.year, rowA.original.month - 1);
        const dateB = new Date(rowB.original.year, rowB.original.month - 1);
        return dateA - dateB;
      }
    },
    {
      Header: 'Skatt senast',
      accessor: 'taxAtLatest',
      Cell: ({ value }) => (
        <span className="date-cell">
          {moment(value).format('YYYY-MM-DD')}
        </span>
      )
    },
    {
      Header: 'Lönespecar',
      accessor: 'numPayslips',
      Cell: ({ value }) => (
        <span className="payslip-badge">
          <Users size={12} />
          {value}
        </span>
      )
    },
    {
      Header: 'Utbetalningsdatum',
      accessor: 'payoutDate',
      Cell: ({ value }) => (
        <span className="date-cell">
          {moment(value).format('YYYY-MM-DD')}
        </span>
      )
    },
    {
      Header: "",
      accessor: 'actions',
      disableFilters: true,
      disableSortBy: true,
      Cell: ({ row }) => (
        <div className="action-cell" onClick={(e) => e.stopPropagation()}>
          <button
            className="action-icon"
            disabled={!onEditPayoutDate} onClick={() => onEditPayoutDate?.(row.original)}
            title="Redigera utbetalningsdatum"
          >
            <Edit3 size={14} />
          </button>
        </div>
      )
    }
  ], [onEditPayoutDate]);

  const handleRowClick = (row) => {
    history.push(`/${companyId}/payroll/${row.original.year}/${row.original.month}`);
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize }
  } = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        pageSize: 10,
        sortBy: [{ id: 'perioden', desc: true }]
      }
    },
    useFilters,
    useSortBy,
    usePagination
  );

  return (
    <>
      {/* Table Header with Search */}
      <div className="table-header">
        <div className="search-container">
          <Search size={16} className="search-icon" />
          <input
            type="text"
            placeholder="Sök period, år eller månad..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button onClick={onClearSearch} className="search-clear">
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table {...getTableProps()} className="modern-table">
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    className={column.className || ''}
                  >
                    <div className="sort-header">
                      {column.render('Header')}
                      <span className="sort-indicator">
                        {column.isSorted
                          ? column.isSortedDesc
                            ? '↓'
                            : '↑'
                          : '↕'}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map(row => {
              prepareRow(row);
              return (
                <tr
                  {...row.getRowProps()}
                  onClick={() => handleRowClick(row)}
                >
                  {row.cells.map(cell => (
                    <td
                      {...cell.getCellProps()}
                      className={cell.column.className || ''}
                    >
                      {cell.render('Cell')}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="pagination-container">
        <div className="pagination-info">
          Visa
          <select
            className="page-size-select"
            value={pageSize}
            onChange={e => setPageSize(Number(e.target.value))}
          >
            {[10, 25, 50].map(size => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          av {data.length} löneperioder
        </div>

        <div className="pagination-buttons">
          <button
            className="pagination-btn"
            onClick={() => gotoPage(0)}
            disabled={!canPreviousPage}
            title="Första sidan"
          >
            <ChevronsLeft size={14} />
          </button>
          <button
            className="pagination-btn"
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
            title="Föregående sida"
          >
            <ChevronLeft size={14} />
          </button>
          <span className="pagination-btn" style={{ cursor: 'default' }}>
            Sida {pageIndex + 1} av {pageCount || 1}
          </span>
          <button
            className="pagination-btn"
            onClick={() => nextPage()}
            disabled={!canNextPage}
            title="Nästa sida"
          >
            <ChevronRight size={14} />
          </button>
          <button
            className="pagination-btn"
            onClick={() => gotoPage(pageCount - 1)}
            disabled={!canNextPage}
            title="Sista sidan"
          >
            <ChevronsRight size={14} />
          </button>
        </div>
      </div>
    </>
  );
};

export default Payrolls;
